/**
 * <concern-testimonial-slider> — the slider behind the PDP "Concern testimonial" block.
 *
 * Markup contract (see snippets/concern-testimonial.liquid):
 *   [data-ct-track]  scroll-snap container
 *   [data-ct-slide]  one testimonial card
 *   [data-ct-dot]    pagination dot, index in data-ct-dot
 *   [data-ct-prev] / [data-ct-next]  optional arrows
 *
 * Direction-agnostic on purpose: positions are read from getBoundingClientRect and
 * moved with scrollBy deltas, so RTL needs no special-casing (scrollLeft is signed
 * differently per engine in RTL — we never read it).
 */
class ConcernTestimonialSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ct-track]');
    if (!this.track) return;

    this.slides = Array.from(this.querySelectorAll('[data-ct-slide]'));
    if (this.slides.length < 2) return;

    this.dots = Array.from(this.querySelectorAll('[data-ct-dot]'));
    this.prevBtn = this.querySelector('[data-ct-prev]');
    this.nextBtn = this.querySelector('[data-ct-next]');

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplayDelay = parseInt(this.dataset.autoplay, 10) || 0;
    this.index = 0;
    this.paused = false;
    this.visible = true;

    this.onScroll = this.throttle(() => this.syncFromScroll());
    this.onPrev = () => this.goTo(this.index - 1, true);
    this.onNext = () => this.goTo(this.index + 1, true);
    this.onDot = (event) => this.goTo(Number(event.currentTarget.dataset.ctDot), true);
    this.onEnter = () => this.pause();
    this.onLeave = () => this.resume();
    this.onVisibility = () => (document.hidden ? this.stopTimer() : this.startTimer());

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.prevBtn?.addEventListener('click', this.onPrev);
    this.nextBtn?.addEventListener('click', this.onNext);
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDot));

    if (this.autoplayDelay > 0 && !this.reducedMotion) {
      this.addEventListener('pointerenter', this.onEnter);
      this.addEventListener('pointerleave', this.onLeave);
      this.addEventListener('focusin', this.onEnter);
      this.addEventListener('focusout', this.onLeave);
      this.addEventListener('touchstart', this.onEnter, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);

      // Only run the timer while the block is actually on screen.
      this.observer = new IntersectionObserver(
        (entries) => {
          this.visible = entries[0].isIntersecting;
          this.visible ? this.startTimer() : this.stopTimer();
        },
        { threshold: 0.4 }
      );
      this.observer.observe(this);
    }

    this.render();
  }

  disconnectedCallback() {
    this.stopTimer();
    this.observer?.disconnect();
    this.track?.removeEventListener('scroll', this.onScroll);
    this.prevBtn?.removeEventListener('click', this.onPrev);
    this.nextBtn?.removeEventListener('click', this.onNext);
    this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDot));
    this.removeEventListener('pointerenter', this.onEnter);
    this.removeEventListener('pointerleave', this.onLeave);
    this.removeEventListener('focusin', this.onEnter);
    this.removeEventListener('focusout', this.onLeave);
    this.removeEventListener('touchstart', this.onEnter);
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  /** rAF-coalesced wrapper — scroll fires far more often than we need to repaint dots. */
  throttle(fn) {
    let frame = null;
    return () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        fn();
      });
    };
  }

  /** Index of the slide whose centre sits closest to the track's centre. */
  currentIndex() {
    const trackRect = this.track.getBoundingClientRect();
    const centre = trackRect.left + trackRect.width / 2;
    let best = 0;
    let bestDistance = Infinity;

    this.slides.forEach((slide, i) => {
      const rect = slide.getBoundingClientRect();
      const distance = Math.abs(rect.left + rect.width / 2 - centre);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });

    return best;
  }

  syncFromScroll() {
    const index = this.currentIndex();
    if (index === this.index) return;
    this.index = index;
    this.render();
  }

  goTo(index, userInitiated) {
    const total = this.slides.length;
    const target = ((index % total) + total) % total; // wraps both ways
    const trackRect = this.track.getBoundingClientRect();
    const slideRect = this.slides[target].getBoundingClientRect();

    // Visual delta — correct in LTR and RTL alike. 'instant' rather than 'auto',
    // since 'auto' would defer to the track's `scroll-behavior: smooth`.
    this.track.scrollBy({
      left: slideRect.left - trackRect.left,
      behavior: this.reducedMotion ? 'instant' : 'smooth',
    });

    this.index = target;
    this.render();

    if (userInitiated) this.restartTimer();
  }

  /**
   * Dots only. The slides stay in the a11y tree and the tab order — the track is a
   * plain scroll region, so every card remains reachable by swipe, keyboard and
   * screen reader regardless of which one is snapped into view.
   */
  render() {
    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('ct__dot--active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  startTimer() {
    if (this.autoplayDelay <= 0 || this.reducedMotion || this.paused || !this.visible) return;
    this.stopTimer();
    this.timer = setInterval(() => this.goTo(this.index + 1, false), this.autoplayDelay);
  }

  stopTimer() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  restartTimer() {
    this.stopTimer();
    this.startTimer();
  }

  pause() {
    this.paused = true;
    this.stopTimer();
  }

  resume() {
    this.paused = false;
    this.startTimer();
  }
}

if (!customElements.get('concern-testimonial-slider')) {
  customElements.define('concern-testimonial-slider', ConcernTestimonialSlider);
}
